import React, { useState, useRef } from "react";
import moment from "moment-timezone";
import { TrashIcon } from "@heroicons/react/24/outline";
import { DatePicker } from "rsuite";
import "rsuite/dist/rsuite-no-reset.min.css";
import { AccessCodeI } from "../../types";
import Button from "../../componentLibrary/Button";
import Input from "../../componentLibrary/Input";
import Modal from "../../componentLibrary/Modal";
import { parseInputFieldValue } from "../CreateLessonPage/utils/dateTimeUtils";
import { DateTimeTypeEnum } from "../../types";

interface DatePickerRef {
  root?: {
    querySelector?: (selector: string) => HTMLElement | null;
  };
}

interface AccessCodeModalProps {
  accessCode: AccessCodeI;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedCode: Partial<AccessCodeI>) => void;
  onDelete: () => void;
}

const AccessCodeModal: React.FC<AccessCodeModalProps> = ({
  accessCode,
  isOpen,
  onClose,
  onSave,
  onDelete,
}) => {
  const [limitCount, setLimitCount] = useState<string>(
    accessCode.limitCount?.toString() || "",
  );
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    accessCode.expirationDate
      ? moment.utc(accessCode.expirationDate).local().toDate()
      : null,
  );
  const [error, setError] = useState<string>("");
  const [dateInputError, setDateInputError] = useState<string>("");
  const datePickerRef = useRef<DatePickerRef | null>(null);
  const pendingCalendarValue = useRef<Date | null>(null);

  const handleSave = () => {
    setError("");

    // Check if there's a date input error - prevent saving with invalid date input
    if (dateInputError) {
      setError(dateInputError);
      return;
    }

    // Calculate original values once for comparison
    const originalLimitCount = accessCode.limitCount?.toString() || "";
    const originalExpirationDate = accessCode.expirationDate
      ? moment.utc(accessCode.expirationDate).local().toDate().getTime()
      : null;

    // Validate limit count
    if (limitCount) {
      const limitValue = parseInt(limitCount, 10);

      if (isNaN(limitValue) || limitValue < 0) {
        setError("Limit count must be a valid positive number");
        return;
      }

      if (limitValue < accessCode.redeemedCount) {
        setError(
          `Limit count cannot be less than redeemed count (${accessCode.redeemedCount})`,
        );
        return;
      }
    }

    // Validate expiration date - always validate if present
    if (expirationDate) {
      // Check for Invalid Date - getTime() returns NaN for invalid dates
      if (isNaN(expirationDate.getTime())) {
        setError("Invalid expiration date");
        return;
      }

      const now = new Date();

      if (expirationDate < now) {
        setError("Expiration date cannot be in the past");
        return;
      }
    }

    // Prepare update data - only include fields that have been modified
    const updates: Partial<
      AccessCodeI & { limitCount: number | null; expirationDate: Date | null }
    > = {};

    // Only include limitCount if changed
    if (limitCount !== originalLimitCount) {
      if (limitCount) {
        updates.limitCount = parseInt(limitCount, 10);
      } else {
        updates.limitCount = null; // Use null to clear the field
      }
    }

    // Only include expirationDate if changed
    const currentExpirationTime = expirationDate
      ? isNaN(expirationDate.getTime())
        ? null
        : expirationDate.getTime()
      : null;
    if (currentExpirationTime !== originalExpirationDate) {
      if (expirationDate && !isNaN(expirationDate.getTime())) {
        // If the time is exactly midnight (00:00:00), default to end of day (23:59:59)
        const dateToSave = new Date(expirationDate);
        if (
          dateToSave.getHours() === 0 &&
          dateToSave.getMinutes() === 0 &&
          dateToSave.getSeconds() === 0
        ) {
          dateToSave.setHours(23, 59, 59, 999);
        }
        updates.expirationDate = dateToSave;
      } else {
        updates.expirationDate = null; // Use null to clear the field
      }
    }

    onSave(updates);
  };

  const handleCancel = () => {
    setError("");
    setDateInputError("");
    setLimitCount(accessCode.limitCount?.toString() || "");
    setExpirationDate(
      accessCode.expirationDate
        ? moment.utc(accessCode.expirationDate).local().toDate()
        : null,
    );
    onClose();
  };

  // Handler to change expiration date (called when calendar changes)
  const handleExpirationDateChange = (date: Date | null) => {
    // Track pending calendar selection
    pendingCalendarValue.current = date;
  };

  // Handler when the date picker closes
  const handleDatePickerClose = () => {
    // First, try to parse input field value (handles dates without time)
    let dateToSet: Date | null = null;

    if (datePickerRef.current) {
      const inputElement = datePickerRef.current?.root?.querySelector(
        "input",
      ) as HTMLInputElement;
      if (inputElement?.value) {
        const inputValue = inputElement.value.trim();
        const parsed = parseInputFieldValue(
          inputValue,
          DateTimeTypeEnum.DATE_TIME,
        );
        if (parsed.isValid && parsed.value) {
          dateToSet = parsed.value;

          // Check if time was explicitly provided in the input
          // If input doesn't contain time indicators (AM/PM or colon after date), default to 11:59 PM
          const hasTimeComponent = /\d{1,2}:\d{2}|AM|PM/i.test(
            inputValue.split(" ").slice(1).join(" "),
          );

          if (!hasTimeComponent) {
            // No time was provided, default to end of day (11:59 PM)
            dateToSet.setHours(23, 59, 59, 999);
          }
        }
      }
    }

    // If no valid input value, use pending calendar selection
    if (!dateToSet && pendingCalendarValue.current) {
      const calendarValue = pendingCalendarValue.current;
      if (!isNaN(calendarValue.getTime())) {
        dateToSet = new Date(calendarValue);
        // If the time is exactly midnight (00:00:00), default to end of day (23:59:59)
        if (
          dateToSet.getHours() === 0 &&
          dateToSet.getMinutes() === 0 &&
          dateToSet.getSeconds() === 0
        ) {
          dateToSet.setHours(23, 59, 59, 999);
        }
      }
    }

    // Update the expiration date state
    if (dateToSet && !isNaN(dateToSet.getTime())) {
      setExpirationDate(dateToSet);
      setDateInputError("");
    } else if (
      pendingCalendarValue.current &&
      isNaN(pendingCalendarValue.current.getTime())
    ) {
      // Only show error if calendar returned invalid date
      setDateInputError("Please enter a complete date and time.");
    }

    // Clear pending value
    pendingCalendarValue.current = null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onCloseModal={handleCancel}
      title="Access Code Details"
      size="md"
      footer={
        <div className="flex justify-between w-full">
          <Button
            onClick={onDelete}
            variant="cautionary"
            className="flex items-center gap-2"
          >
            <TrashIcon className="w-5 h-5" />
            Delete
          </Button>
          <div className="flex gap-3">
            <Button onClick={handleCancel} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleSave} variant="dark">
              Save
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-bold mb-2 text-gray-700">
            Access Code
          </label>
          <div className="w-full h-10 px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 flex items-center">
            {accessCode.accessCode}
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold mb-2 text-gray-700">
            Segment
          </label>
          <div className="w-full h-10 px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 flex items-center">
            {accessCode.segment}
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold mb-2 text-gray-700">
            Redeemed Count
          </label>
          <div className="w-full h-10 px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 flex items-center">
            {accessCode.redeemedCount}
          </div>
        </div>

        <div>
          <Input
            label="Limit Count"
            type="number"
            value={limitCount}
            onChange={(e) => setLimitCount(e.target.value)}
            placeholder="No limit"
            min={accessCode.redeemedCount}
          />
          <p className="text-xs text-gray-500 mt-1">
            Leave empty for no limit. Must be greater than or equal to redeemed
            count ({accessCode.redeemedCount}).
          </p>
        </div>

        <div>
          <label className="block text-sm font-bold mb-2 text-gray-700">
            Expiration Date
          </label>
          <DatePicker
            ref={datePickerRef as React.Ref<HTMLDivElement>}
            value={expirationDate}
            onChange={handleExpirationDateChange}
            onClose={handleDatePickerClose}
            onClean={() => {
              setExpirationDate(null);
              setDateInputError("");
            }}
            format="MM/dd/yyyy hh:mm aa"
            cleanable={true}
            placeholder="No expiration"
            showMeridian
            placement="topStart"
            className="w-full"
            style={{ width: "100%", zIndex: 9999 }}
            menuStyle={{ zIndex: 9999 }}
          />
          {dateInputError && (
            <p className="text-red-500 text-sm mt-1">{dateInputError}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Leave empty for no expiration. Time defaults to 11:59 PM if not
            specified.
          </p>
        </div>

        <div>
          <label className="block text-sm font-bold mb-2 text-gray-700">
            Status
          </label>
          <div className="w-full h-10 px-4 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center">
            <span
              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                accessCode.isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {accessCode.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {accessCode.createdAt && (
            <div>
              <label className="block text-sm font-bold mb-2 text-gray-700">
                Created At
              </label>
              <div className="w-full h-10 px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 flex items-center text-sm">
                {new Date(accessCode.createdAt).toLocaleDateString()}
              </div>
            </div>
          )}
          {accessCode.updatedAt && (
            <div>
              <label className="block text-sm font-bold mb-2 text-gray-700">
                Updated At
              </label>
              <div className="w-full h-10 px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 flex items-center text-sm">
                {new Date(accessCode.updatedAt).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600 font-medium">{error}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AccessCodeModal;
